import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const ThemeContext = createContext();

export const useTheme = () => {
  return useContext(ThemeContext);
};

export const ThemeProvider = ({ children }) => {
  // Check user's system preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Get stored theme preference from localStorage, default to system preference
  const storedTheme = localStorage.getItem('themeMode');
  const initialThemeMode = storedTheme ? storedTheme : prefersDarkMode ? 'dark' : 'light';
  
  // State for theme mode
  const [themeMode, setThemeMode] = useState(initialThemeMode);

  // Create theme
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          primary: {
            main: themeMode === 'dark' ? '#a5cdff' : '#4285F4', // New blue for light mode
            dark: themeMode === 'dark' ? '#7fb6ff' : '#3367d6',
            light: themeMode === 'dark' ? '#cae5ff' : '#8ab4f8',
            contrastText: themeMode === 'dark' ? '#121212' : '#ffffff',
          },
          secondary: {
            main: themeMode === 'dark' ? '#ffc3ee' : '#ffb5e8', // Brighter pastel pink for dark mode
            dark: themeMode === 'dark' ? '#ffaadf' : '#df8fc9',
            light: themeMode === 'dark' ? '#ffe0f7' : '#ffd6f3',
            contrastText: themeMode === 'dark' ? '#121212' : '#333333',
          },
          info: {
            main: themeMode === 'dark' ? '#b4eaff' : '#9de3fd', // Brighter pastel light blue for dark mode
            dark: themeMode === 'dark' ? '#8ad8ff' : '#6bbada',
            light: themeMode === 'dark' ? '#d4f3ff' : '#c4f0ff',
            contrastText: themeMode === 'dark' ? '#121212' : '#333333',
          },
          success: {
            main: themeMode === 'dark' ? '#c4f5e5' : '#b5ead7', // Brighter pastel mint for dark mode
            dark: themeMode === 'dark' ? '#a3ebd1' : '#7fc7b5',
            light: themeMode === 'dark' ? '#e8fff7' : '#dffff3',
            contrastText: themeMode === 'dark' ? '#121212' : '#333333',
          },
          warning: {
            main: themeMode === 'dark' ? '#fffdcc' : '#fdffb6', // Brighter pastel yellow for dark mode
            dark: themeMode === 'dark' ? '#fff9a3' : '#d9db8c',
            light: themeMode === 'dark' ? '#fffef0' : '#ffffdf',
            contrastText: themeMode === 'dark' ? '#121212' : '#333333',
          },
          error: {
            main: themeMode === 'dark' ? '#ffb3ba' : '#ff9aa2', // Brighter pastel salmon for dark mode
            dark: themeMode === 'dark' ? '#ff949e' : '#db7880',
            light: themeMode === 'dark' ? '#ffd4d9' : '#ffccd0',
            contrastText: themeMode === 'dark' ? '#121212' : '#333333',
          },
          ...(themeMode === 'dark'
            ? {
                background: {
                  default: '#35363c', // Darker purple-blue background
                  paper: 'rgba(40, 42, 66, 0.85)', // Transparent dark purple-blue
                },
                text: {
                  primary: '#e8e8fd', // Lighter text for dark mode
                  secondary: '#b4b8e2', // Lighter secondary text with purple tint
                },
                divider: 'rgba(165, 205, 255, 0.15)', // Pastel blue tinted divider
              }
            : {
                background: {
                  default: '#dadae2', // Off-white background
                  paper: 'rgba(252, 252, 252, 0.85)', // Transparent off-white
                },
                text: {
                  primary: '#3a3f4b', // Darker, easier on the eyes than black
                  secondary: '#5f6577', // Softer secondary text
                },
                divider: 'rgba(187, 200, 224, 0.2)', // Soft blue-gray divider
              }),
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '-0.025em',
          },
          h2: {
            fontSize: '1.75rem',
            fontWeight: 700,
            letterSpacing: '-0.025em',
          },
          h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.025em',
          },
          h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            letterSpacing: '-0.025em',
          },
          h5: {
            fontSize: '1.1rem',
            fontWeight: 600,
          },
          h6: {
            fontSize: '1rem',
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 16,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: `
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
              
              ::-webkit-scrollbar {
                width: 8px;
                height: 8px;
              }
              ::-webkit-scrollbar-track {
                background: ${themeMode === 'dark' ? '#1e293b' : '#f1f5f9'};
              }
              ::-webkit-scrollbar-thumb {
                background: ${themeMode === 'dark' ? '#475569' : '#cbd5e1'};
                border-radius: 4px;
              }
              ::-webkit-scrollbar-thumb:hover {
                background: ${themeMode === 'dark' ? '#64748b' : '#94a3b8'};
              }
            `,
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: '10px',
                padding: '8px 20px',
                boxShadow: 'none',
                fontWeight: 600,
                color: themeMode === 'dark'
                  ? '#a5cdff' // Light blue text for dark mode
                  : '#4285F4', // Blue text for light mode
                '&:hover': {
                  boxShadow: themeMode === 'dark' 
                    ? '0 4px 12px rgba(148, 194, 253, 0.3)' // Pastel blue shadow
                    : '0 4px 12px rgba(148, 194, 253, 0.4)', // Pastel blue shadow
                },
              },
              contained: {
                backdropFilter: 'blur(10px)',
                background: themeMode === 'dark'
                  ? 'linear-gradient(145deg, #94c2fd, #6a9eda)' // Pastel blue gradient
                  : 'linear-gradient(145deg, #bcd6fe, #94c2fd)', // Pastel blue gradient
                color: themeMode === 'dark' 
                  ? '#ffffff'  // White text for dark mode
                  : '#333333', // Dark text for light mode
                '&:hover': {
                  background: themeMode === 'dark'
                    ? 'linear-gradient(145deg, #6a9eda, #94c2fd)' // Reversed gradient
                    : 'linear-gradient(145deg, #94c2fd, #bcd6fe)', // Reversed gradient
                },
              },
              containedSecondary: {
                background: themeMode === 'dark'
                  ? 'linear-gradient(145deg, #ffb5e8, #df8fc9)' // Pastel pink gradient
                  : 'linear-gradient(145deg, #ffd6f3, #ffb5e8)', // Pastel pink gradient
                color: themeMode === 'dark' 
                  ? '#ffffff'  // White text for dark mode
                  : '#333333', // Dark text for light mode
                '&:hover': {
                  background: themeMode === 'dark'
                    ? 'linear-gradient(145deg, #df8fc9, #ffb5e8)' // Reversed gradient
                    : 'linear-gradient(145deg, #ffb5e8, #ffd6f3)', // Reversed gradient
                },
              },
              outlined: {
                borderColor: themeMode === 'dark'
                  ? 'rgba(148, 194, 253, 0.5)' // Pastel blue border
                  : 'rgba(148, 194, 253, 0.7)', // Pastel blue border
                color: themeMode === 'dark'
                  ? '#a5cdff' // Light blue text for dark mode
                  : '#4285F4', // Blue text for light mode
                '&:hover': {
                  borderColor: themeMode === 'dark'
                    ? 'rgba(148, 194, 253, 0.8)' // Pastel blue border
                    : 'rgba(148, 194, 253, 1)', // Pastel blue border
                  backgroundColor: themeMode === 'dark'
                    ? 'rgba(148, 194, 253, 0.1)' // Pastel blue background
                    : 'rgba(148, 194, 253, 0.1)', // Pastel blue background
                },
              },
              outlinedSecondary: {
                borderColor: themeMode === 'dark'
                  ? 'rgba(255, 195, 238, 0.5)' // Pastel pink border
                  : 'rgba(255, 181, 232, 0.7)', // Pastel pink border
                color: themeMode === 'dark'
                  ? '#ffc3ee' // Light pink text for dark mode
                  : '#df8fc9', // Pink text for light mode
                '&:hover': {
                  borderColor: themeMode === 'dark'
                    ? 'rgba(255, 195, 238, 0.8)' // Pastel pink border
                    : 'rgba(255, 181, 232, 1)', // Pastel pink border
                  backgroundColor: themeMode === 'dark'
                    ? 'rgba(255, 195, 238, 0.1)' // Pastel pink background
                    : 'rgba(255, 181, 232, 0.1)', // Pastel pink background
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: themeMode === 'dark' 
                  ? '0 4px 20px rgba(165, 205, 255, 0.15)' // Pastel blue shadow
                  : '0 4px 20px rgba(0, 0, 0, 0.05)',
                border: themeMode === 'dark'
                  ? '1px solid rgba(165, 205, 255, 0.2)' // Pastel blue border
                  : '1px solid rgba(148, 194, 253, 0.3)', // Pastel blue border
                background: themeMode === 'dark'
                  ? 'linear-gradient(145deg, rgba(99, 99, 103, 0.9), rgba(12, 13, 20, 0.9))'
                  : 'linear-gradient(145deg, rgba(246, 248, 255, 0.85), rgba(239, 241, 253, 0.85))',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backdropFilter: 'blur(10px)',
                boxShadow: themeMode === 'dark' 
                  ? '0 4px 20px rgba(165, 205, 255, 0.15)' // Pastel blue shadow
                  : '0 4px 20px rgba(148, 194, 253, 0.2)', // Pastel blue shadow
                border: themeMode === 'dark'
                  ? '1px solid rgba(165, 205, 255, 0.2)' // Pastel blue border
                  : '1px solid rgba(148, 194, 253, 0.3)', // Pastel blue border
                background: themeMode === 'dark'
                  ? 'linear-gradient(145deg, rgba(99, 99, 103, 0.9), rgba(12, 13, 20, 0.9))'
                  : 'linear-gradient(145deg, rgba(246, 248, 255, 0.85), rgba(239, 241, 253, 0.85))',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: themeMode === 'dark' 
                    ? '0 8px 30px rgba(165, 205, 255, 0.25)' // Stronger pastel blue shadow
                    : '0 8px 30px rgba(148, 194, 253, 0.3)', // Pastel blue shadow
                  border: themeMode === 'dark'
                    ? '1px solid rgba(165, 205, 255, 0.4)' // Brighter pastel blue border on hover
                    : '1px solid rgba(148, 194, 253, 0.5)', // Pastel blue border
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backdropFilter: 'blur(10px)',
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(40, 42, 66, 0.85)'
                  : 'rgba(248, 248, 253, 0.8)',
                boxShadow: themeMode === 'dark'
                  ? '0 4px 10px rgba(165, 205, 255, 0.15)' // Pastel blue shadow
                  : '0 4px 10px rgba(148, 194, 253, 0.15)', // Pastel blue shadow
                borderBottom: themeMode === 'dark'
                  ? '1px solid rgba(165, 205, 255, 0.2)' // Pastel blue border
                  : '1px solid rgba(148, 194, 253, 0.2)', // Pastel blue border
                background: themeMode === 'dark'
                  ? 'linear-gradient(to right, rgba(72, 73, 78, 0.85), rgba(4, 5, 14, 0.85))'
                  : 'linear-gradient(to right, rgba(246, 248, 255, 0.85), rgba(237, 240, 252, 0.85))',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backdropFilter: 'blur(10px)',
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(40, 42, 66, 0.9)'
                  : 'rgba(248, 248, 253, 0.9)',
                boxShadow: themeMode === 'dark'
                  ? '4px 0 15px rgba(165, 205, 255, 0.15)' // Pastel blue shadow
                  : '4px 0 15px rgba(148, 194, 253, 0.2)', // Pastel blue shadow
                borderRight: themeMode === 'dark'
                  ? '1px solid rgba(165, 205, 255, 0.2)' // Pastel blue border
                  : '1px solid rgba(148, 194, 253, 0.2)', // Pastel blue border
                background: themeMode === 'dark'
                  ? 'linear-gradient(rgba(72, 73, 78, 0.9), rgba(7, 8, 9, 0.9))'
                  : 'linear-gradient(to bottom, rgba(246, 248, 255, 0.85), rgba(237, 240, 252, 0.85))',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: {
                fontWeight: 700,
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(42, 42, 51, 0.7)'
                  : 'rgba(244, 247, 255, 0.5)',
                borderBottom: themeMode === 'dark'
                  ? '2px solid rgba(165, 205, 255, 0.3)' // Brighter pastel blue border
                  : '2px solid rgba(148, 194, 253, 0.3)', // Pastel blue border
                color: themeMode === 'dark' ? '#e8e8fd' : undefined,
              },
              root: {
                backdropFilter: 'blur(8px)',
                borderBottom: themeMode === 'dark'
                  ? '1px solid rgba(165, 205, 255, 0.15)' // Pastel blue border
                  : '1px solid rgba(148, 194, 253, 0.1)', // Lighter pastel blue border
                color: themeMode === 'dark' ? '#e8e8fd' : undefined,
              }
            },
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                borderRadius: '10px',
                marginBottom: '4px',
                '&.Mui-selected': {
                  backgroundColor: themeMode === 'dark'
                    ? 'rgba(165, 205, 255, 0.25)' // Brighter pastel blue
                    : 'rgba(148, 194, 253, 0.2)', // Pastel blue
                  '&:hover': {
                    backgroundColor: themeMode === 'dark'
                      ? 'rgba(165, 205, 255, 0.35)' // Brighter pastel blue
                      : 'rgba(148, 194, 253, 0.3)', // Pastel blue
                  },
                },
                '&:hover': {
                  backgroundColor: themeMode === 'dark'
                    ? 'rgba(165, 205, 255, 0.15)' // Light pastel blue
                    : 'rgba(148, 194, 253, 0.1)', // Very light pastel blue
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                fontWeight: 600,
                backdropFilter: 'blur(8px)',
                border: themeMode === 'dark'
                  ? '1px solid rgba(165, 205, 255, 0.25)' // Brighter pastel blue border
                  : '1px solid rgba(148, 194, 253, 0.2)', // Pastel blue border
                color: themeMode === 'dark' ? '#e8e8fd' : undefined,
              },
              filled: {
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(40, 42, 66, 0.9)'
                  : 'rgba(244, 247, 255, 0.9)',
              },
              colorPrimary: {
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(165, 205, 255, 0.2)' // Pastel blue background
                  : undefined,
                color: themeMode === 'dark' ? '#ffffff' : undefined,
              },
              colorSecondary: {
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(255, 195, 238, 0.2)' // Pastel pink background
                  : undefined,
                color: themeMode === 'dark' ? '#ffffff' : undefined,
              },
              colorSuccess: {
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(196, 245, 229, 0.2)' // Pastel mint background
                  : undefined,
                color: themeMode === 'dark' ? '#ffffff' : undefined,
              },
              colorError: {
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(255, 179, 186, 0.2)' // Pastel salmon background
                  : undefined,
                color: themeMode === 'dark' ? '#ffffff' : undefined,
              },
              colorWarning: {
                backgroundColor: themeMode === 'dark'
                  ? 'rgba(255, 253, 204, 0.2)' // Pastel yellow background
                  : undefined,
                color: themeMode === 'dark' ? '#333333' : undefined,
              },
            },
          },
        },
      }),
    [themeMode]
  );

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newTheme);
    localStorage.setItem('themeMode', newTheme);
  };

  // Set theme explicitly
  const setTheme = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
  };

  const value = {
    themeMode,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};